import React, { useState, useEffect } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../utils/api.js';
import { Button, Input } from '../components/legacy-ui.js';
import { AppPageHeader, AppTable, AppModalShell } from '../components/app-ui.js';
import { TagsInput } from '../components/ui/tags-input.js';
import { formatCurrency } from '../utils/formatters.js';
import { Plus, Package, Pencil, ChevronLeft, ChevronRight, Trash2, UploadCloud } from 'lucide-react';
import { useAuth } from '../context/AuthContext.js';
import { Helmet } from 'react-helmet';
import { companyPageTitle } from '../utils/company.js';
import toast from 'react-hot-toast';
import { LoadingState } from '../components/motion.js';

const ProductsModule = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const emptyForm = () => ({
    name: '',
    price: '',
    byWeight: false,
    imageUrl: '',
    alias: [],
    promedioMin: '',
    promedioMax: '',
  });
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fmtPromedioBound = (product) => {
    const min = product?.advanced?.promedioMin;
    const max = product?.advanced?.promedioMax;
    const hasMin = min != null && min !== '';
    const hasMax = max != null && max !== '';
    if (!hasMin && !hasMax) return '—';
    if (hasMin && hasMax) return `${Number(min)} – ${Number(max)} L/kg`;
    if (hasMax) return `máx ${Number(max)} L/kg`;
    return `mín ${Number(min)} L/kg`;
  };

  const fetchProducts = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getProducts({ page });
      setProducts(res.data.data);
      setCurrentPage(res.data.currentPage);
      setTotalPages(res.data.totalPages);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchProducts(currentPage);
    }
  }, [user, currentPage]);

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData(emptyForm());
    setImageFile(null);
    setImagePreview('');
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData(emptyForm());
    setImageFile(null);
    setImagePreview('');
    setShowForm(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const minRaw = String(formData.promedioMin || '').trim();
    const maxRaw = String(formData.promedioMax || '').trim();
    const min = minRaw === '' ? null : Number(minRaw);
    const max = maxRaw === '' ? null : Number(maxRaw);
    if (minRaw && !Number.isFinite(min)) {
      toast.error('Promedio mínimo inválido', { position: 'top-center' });
      return;
    }
    if (maxRaw && !Number.isFinite(max)) {
      toast.error('Promedio máximo inválido', { position: 'top-center' });
      return;
    }
    if (min != null && max != null && min > max) {
      toast.error('El promedio mínimo no puede ser mayor que el máximo', { position: 'top-center' });
      return;
    }
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('price', Number(formData.price));
      data.append('alias', formData.alias);
      data.append('advanced[byWeight]', formData.byWeight);
      data.append('advanced[promedioMin]', min == null ? '' : String(min));
      data.append('advanced[promedioMax]', max == null ? '' : String(max));

      if (!imageFile && formData.imageUrl) {
        data.append('imageUrl', formData.imageUrl);
      }

      if (imageFile) {
        data.append('image', imageFile);
      }

      if (editingProduct) {
        await updateProduct(editingProduct._id, data);
        toast.success("Producto actualizado", { position: "top-center" });
      } else {
        await createProduct(data);
        toast.success("Producto creado", { position: "top-center" });
      }
      resetForm();
      fetchProducts(editingProduct ? currentPage : 1);
    } catch (error) {
      console.error("Error saving product: ", error);
      toast.error(
        `Error al guardar el producto: ${error.response?.data?.error || error.message}`,
        { position: "top-center" },
      );
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      price: product.price || '',
      byWeight: product.advanced?.byWeight || false,
      imageUrl: product.imageUrl || '',
      alias: product.alias || [],
      promedioMin:
        product.advanced?.promedioMin != null && product.advanced?.promedioMin !== ''
          ? String(product.advanced.promedioMin)
          : '',
      promedioMax:
        product.advanced?.promedioMax != null && product.advanced?.promedioMax !== ''
          ? String(product.advanced.promedioMax)
          : '',
    });
    setImagePreview(product.imageUrl || '');
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      try {
        await deleteProduct(productId);
        toast.success("Producto eliminado", { position: "top-center" });
        fetchProducts(currentPage);
      } catch (error) {
        console.error("Error deleting product: ", error);
        toast.error(error.response?.data?.error || "No se pudo eliminar el producto", {
          position: "top-center",
        });
      }
    }
  };

  const ProductForm = (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2 space-y-4">
          <Input label="Nombre del Producto" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <Input label="Precio Unitario ($)" type="number" step="0.01" required value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Imagen</label>
          <label htmlFor="image-upload" className="cursor-pointer block w-full h-28 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50">
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" className="h-full w-full object-cover rounded-lg" />
            ) : (
              <div className="text-center">
                <UploadCloud size={24} className="mx-auto" />
                <p className="text-xs mt-1">Subir imagen</p>
              </div>
            )}
          </label>
          <input id="image-upload" type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
        </div>
      </div>
      <div className="md:col-span-2 space-y-2">
        <TagsInput
          label="Alias"
          placeholder="Alias (Presiona Enter o Coma)"
          value={formData.alias}
          onChange={tags => setFormData({ ...formData, alias: tags })}
        />
      </div>
      <div className="flex items-center space-x-2 md:col-span-2 py-2">
        <input type="checkbox" id="byWeight" className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" checked={formData.byWeight} onChange={e => setFormData({ ...formData, byWeight: e.target.checked })} />
        <label htmlFor="byWeight" className="text-gray-700">Se vende por peso (Kg)</label>
      </div>
      <div className="md:col-span-2 border border-amber-100 bg-amber-50/60 rounded-lg p-4 space-y-3">
        <div>
          <p className="text-sm font-semibold text-amber-900">Alerta de promedio (Quesos)</p>
          <p className="text-xs text-amber-800/90 mt-0.5">
            Si una producción queda por encima del máximo (L/kg), se marca en Quesos. Déjalo vacío si no quieres alerta.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Promedio mínimo (L/kg)"
            type="number"
            step="0.01"
            min="0"
            value={formData.promedioMin}
            onChange={(e) => setFormData({ ...formData, promedioMin: e.target.value })}
            placeholder="Opcional"
          />
          <Input
            label="Promedio máximo (L/kg)"
            type="number"
            step="0.01"
            min="0"
            value={formData.promedioMax}
            onChange={(e) => setFormData({ ...formData, promedioMax: e.target.value })}
            placeholder="Ej. 8.5"
          />
        </div>
      </div>
      <div className="md:col-span-2 flex gap-2 justify-end mt-4">
        <Button type="button" variant="secondary" onClick={resetForm}>Cancelar</Button>
        <Button type="submit">{editingProduct ? 'Actualizar' : 'Guardar'}</Button>
      </div>
    </form>
  );

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Productos")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Productos"
            subtitle="Catálogo de productos disponibles"
            actions={
              <Button onClick={handleAddNew}>
                <Plus size={16} /> Nuevo
              </Button>
            }
          />
        </div>

        <div className="app-content">
          {loading ? <LoadingState label="Cargando productos" /> : (
            <AppTable
              columns={[
                { key: "product", title: "Producto" },
                { key: "type", title: "Tipo" },
                { key: "promedio", title: "Rango L/kg" },
                { key: "price", title: "Precio", align: "right" },
                { key: "actions", title: "Acciones", align: "right" },
              ]}
              className="text-sm"
            >
                    {products.length > 0 ? products.map((product) => (
                      <tr key={product._id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/70">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3 min-w-[220px]">
                            {product.imageUrl ? (
                              <img src={product.imageUrl} alt={product.name} className="w-11 h-11 object-cover rounded-lg border border-gray-200" />
                            ) : (
                              <div className="bg-indigo-50 p-2 rounded-lg text-indigo-500 flex-shrink-0">
                                <Package size={20} />
                              </div>
                            )}
                            <span className="font-semibold text-gray-900">{product.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {product.advanced?.byWeight ? 'Por peso (kg)' : 'Por unidad'}
                        </td>
                        <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                          {fmtPromedioBound(product)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-indigo-600 whitespace-nowrap">
                          {formatCurrency(product.price)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleEdit(product)} title="Editar">
                              <Pencil size={18} />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDelete(product._id)} title="Eliminar">
                              <Trash2 size={18} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-gray-500">
                          No hay productos registrados.
                        </td>
                      </tr>
                    )}
            </AppTable>
          )}
        </div>

        <div className="app-footer">
          <div className="flex justify-center items-center gap-3">
            <Button variant="secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></Button>
            <span className="text-sm text-gray-600 font-medium">Página {currentPage} de {totalPages}</span>
            <Button variant="secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></Button>
          </div>
        </div>
      </div>

      <AppModalShell
        open={showForm}
        onClose={resetForm}
        title={`${editingProduct ? 'Editar' : 'Agregar'} Producto`}
        widthClass="max-w-2xl"
      >
        {ProductForm}
      </AppModalShell>
    </>
  );
};

export default ProductsModule;
